// dashboard-bar.component.ts
import { Component, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { COMMON_IMPORTS } from '../common-imports';
import { ButtonModule } from 'primeng/button';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { CalculationType } from '../backend-response-models/calc-enums.helper';
import { TranslatePipe } from '../languages/translate.pipe';
import { DropdownModule } from 'primeng/dropdown';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Language } from '../shared/shared-interfaces/shared.interfaces';
import { ReportType } from '../shared/shared-enums/shared.enums';

@Component({
  selector: 'app-dashboard-bar',
  standalone: true,
  imports: [...COMMON_IMPORTS, ButtonModule, DropdownModule, CheckboxModule, SplitButtonModule],
  templateUrl: './dashboard-bar.component.html',
  styleUrl: './dashboard-bar.component.css',
})
export class DashboardBarComponent implements OnInit {
  @Input() patientIdentifier?: string;
  @Input() calculationType?: CalculationType;
  @Input() reportType?: ReportType;
  @Output() languageDidChange = new EventEmitter<Language>();
  @Output() reportTypeDidChange = new EventEmitter<ReportType>();
  @Output() downloadPDFPressed = new EventEmitter<string>();

  isHidden = false;
  lastScrollTop = 0;
  private scrollThreshold = 60;
  private scrollDebounceTimer?: number = 100;
  private lastToggleTime = 0;
  private minimumToggleDelay = 300; // ms

  pdfItems = [
    {
      label: 'Download Parent PDF',
      icon: 'pi pi-download',
      command: () => {
        this.downloadPDF('parent');
      },
    },
    {
      label: 'Download ECP PDF',
      icon: 'pi pi-download',
      command: () => {
        this.downloadPDF('child');
      },
    },
  ];

  selectedLanguage?: Language;

  languages: Language[] = [
    { label: 'English', code: 'en' },
    { label: 'Spanish', code: 'es' },
    { label: 'French', code: 'fr' },
    { label: 'Chinese', code: 'zh' },
  ];

  title: string = 'PreMO';
  parentView: boolean = false;
  ecpView: boolean = false;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    if (this.scrollDebounceTimer) {
      window.clearTimeout(this.scrollDebounceTimer);
    }

    this.scrollDebounceTimer = window.setTimeout(() => {
      const currentTime = Date.now();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      // Check if enough time has passed since last toggle
      if (currentTime - this.lastToggleTime < this.minimumToggleDelay) {
        return;
      }

      // Check if scroll amount is significant enough
      if (Math.abs(scrollTop - this.lastScrollTop) < this.scrollThreshold) {
        return;
      }

      if (scrollTop > this.lastScrollTop && scrollTop > 100) {
        // Scrolling down and past initial threshold
        if (!this.isHidden) {
          this.isHidden = true;
          this.lastToggleTime = currentTime;
        }
      } else {
        // Scrolling up
        if (this.isHidden) {
          this.isHidden = false;
          this.lastToggleTime = currentTime;
        }
      }

      this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }, 10);
  }

  readonly typeTranslationKeys: Record<CalculationType, string> = {
    [CalculationType.PREMYOPES]: 'PREMYOPIC_TYPE',
    [CalculationType.MYOPES_ONE_VISIT]: 'MYOPIC_SINGLE_TYPE',
    [CalculationType.MYOPES_MULTI_VISITS]: 'MYOPIC_PROGRESSION_TYPE',
    [CalculationType.NO_CALCULATION_PLOTS_ONLY]: 'NO_CALCULATION_PLOTS_ONLY',
  };

  constructor(private translatePipe: TranslatePipe) {}

  ngOnInit(): void {
    if (this.reportType === ReportType.Parent) {
      this.parentView = true;
      this.ecpView = false;
    } else {
      this.parentView = false;
      this.ecpView = true;
    }

    this.refreshAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refreshAll();
  }

  onParentViewCheckboxChange(event: CheckboxChangeEvent) {
    this.parentView = event.checked;
    this.ecpView = !event.checked;
    this.reportTypeDidChange.emit(this.parentView ? ReportType.Parent : ReportType.ECP);
  }

  onECPViewCheckboxChange(event: CheckboxChangeEvent) {
    this.ecpView = event.checked;
    this.parentView = !event.checked;
    this.reportTypeDidChange.emit(this.parentView ? ReportType.Parent : ReportType.ECP);
  }

  downloadPDF(type: string): void {
    // Implement your PDF download logic here
    console.log(`Downloading ${type} PDF`);
  }

  async onLanguageChange(event: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventValue = (event as any).value as Language;
    this.translatePipe.getText.loadDictionary(eventValue.code);
    this.refreshAll();
    this.languageDidChange.emit(eventValue);
  }

  private refreshAll() {
    this.patientIdentifier = this.patientIdentifier ?? ' ';
    const typeKey = this.typeTranslationKeys[this.calculationType ?? CalculationType.PREMYOPES];
    const typeText = this.translatePipe.transform(typeKey);
    this.title = `${this.patientIdentifier} [${typeText}]`;
  }
}
